import { isWeb, isTrackReference } from '@livekit/components-core';
import { RoomEvent, Track } from 'livekit-client';
import * as React from 'react';
import {
  ConnectionStateToast,
  RoomAudioRenderer,
  GridLayout,
  DisconnectButton,
  LeaveIcon,
  useTracks,
  useParticipants,
} from '@livekit/components-react';
import { ParticipantTile } from './ParticipantTile';
import { Box } from '@mui/joy';

export interface VideoConferenceProps extends React.HTMLAttributes<HTMLDivElement> {}

export function VideoConference({ ...props }: VideoConferenceProps) {
  const allParticipants = useParticipants();

  const allTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], {
    onlySubscribed: false,
  });

  const humanParticipant = allParticipants.find(p => !p.identity.startsWith('agent-'));
  const humanVideoTrackRef = allTracks.find(
    tr => tr.participant.identity === humanParticipant?.identity && tr.source === Track.Source.Camera
  );

  const isHumanVideoEnabled = React.useMemo(() => {
    if (!humanVideoTrackRef || !isTrackReference(humanVideoTrackRef)) return false;
    return (
      humanVideoTrackRef.publication?.isSubscribed &&
      !humanVideoTrackRef.publication?.isMuted &&
      humanVideoTrackRef.publication?.track !== undefined
    );
  }, [humanVideoTrackRef]);

  const isHumanVideoOff = !isHumanVideoEnabled;

  const filteredTracks = React.useMemo(() => {
    if (isHumanVideoEnabled) {
      return allTracks;
    } else {
      return allTracks.filter(tr => tr.participant.identity.startsWith('agent-'));
    }
  }, [allTracks, isHumanVideoEnabled]);

  return (
    <div className="lk-video-conference" {...props}>
      {isWeb() && (
        <div
          className="lk-video-conference-inner"
          style={{ position: 'relative', width: '100%', height: '100%' }}
        >
          <div className="lk-grid-layout-wrapper" style={{ height: 'calc(100% - 4rem)' }}>
            <GridLayout tracks={filteredTracks}>
              <ParticipantTile isHumanVideoOff={isHumanVideoOff} />
            </GridLayout>
          </div>
          <Box sx={{
            position: 'absolute',
            bottom: '1rem',
            right: '1rem',
            zIndex: 101,
          }}>
            <DisconnectButton>
              <LeaveIcon />
              {'Leave'}
            </DisconnectButton>
          </Box>
        </div>
      )}
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
